import { useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Building2, FileText, RefreshCw, Smartphone, Eye, EyeOff, Upload, Loader2, Plus, Trash2, PenLine, Zap } from 'lucide-react';
import { formatarMoeda, formatarTelefone } from '@/utils/formatters';
import { useFaturaManual } from '@/hooks/useFaturaManual';
import { tmPelaFidelidade } from '@/lib/fidelidadeConta';
import type { LeadCarteira } from '@/hooks/useLeadsCarteira';

interface LinhaDetalhada {
  numero: string;
  gbAtual: number;
  valorAtual: number;
  planoAtual: string;
  dataFidelidade?: string | null;
}

interface LinhaManual {
  numero: string;
  gbAtual: number;
  valorAtual: number;
}

interface DadosFatura {
  linhas: number;
  gb: number;
  valor: number;
  gb_proposto?: number;
  valor_proposto?: number;
  linhasDetalhadas?: LinhaDetalhada[];
}

interface PlanoAtualRenovacaoProps {
  dadosFatura: DadosFatura | null;
  lead: LeadCarteira;
  cnpj?: string;
  clienteId?: string | null;
  ocultarPlanoAtual?: boolean;
  onToggleOcultar?: () => void;
  onFaturaUpload?: () => void;
  onLinhasManuaisChange?: (linhas: LinhaManual[]) => void;
  onAplicarSugestao?: () => void;
}

const GB_OPTIONS = [0, 4, 6, 10, 15, 20, 25, 30, 40, 45, 50, 60, 80, 100];

export function PlanoAtualRenovacao({ 
  dadosFatura, 
  lead, 
  cnpj,
  clienteId,
  ocultarPlanoAtual, 
  onToggleOcultar,
  onFaturaUpload,
  onLinhasManuaisChange,
  onAplicarSugestao,
}: PlanoAtualRenovacaoProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [linhasManuais, setLinhasManuais] = useState<LinhaManual[]>([{ numero: '', gbAtual: 0, valorAtual: 0 }]);
  
  // Hook para upload manual
  const { uploadAndProcess, isUploading, isProcessing, isLoading } = useFaturaManual({
    cnpj: cnpj || lead.cnpj || '',
    clienteId: clienteId || lead.cliente_id,
    razaoSocial: lead.razao_social || lead.nome_fantasia,
    onSuccess: onFaturaUpload,
  });

  // Handler para seleção de arquivo
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadAndProcess(file);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const updateLinha = (idx: number, field: keyof LinhaManual, value: string | number) => {
    const updated = linhasManuais.map((l, i) => i === idx ? { ...l, [field]: value } : l);
    setLinhasManuais(updated);
    onLinhasManuaisChange?.(updated.filter(l => l.numero.replace(/\D/g, '').length >= 10));
  };

  const addLinha = () => {
    const updated = [...linhasManuais, { numero: '', gbAtual: 0, valorAtual: 0 }];
    setLinhasManuais(updated);
  };

  const removeLinha = (idx: number) => {
    const updated = linhasManuais.filter((_, i) => i !== idx);
    setLinhasManuais(updated);
    onLinhasManuaisChange?.(updated.filter(l => l.numero.replace(/\D/g, '').length >= 10));
  };

  const formatPhone = (val: string) => {
    let digits = val.replace(/\D/g, '');
    if (digits.length > 11) digits = digits.substring(0, 11);
    let formatted = digits;
    if (digits.length > 2) formatted = `(${digits.substring(0, 2)}) ${digits.substring(2)}`;
    if (digits.length > 7) formatted = `(${digits.substring(0, 2)}) ${digits.substring(2, 7)}-${digits.substring(7)}`;
    return formatted;
  };

  // Se não tem dados da fatura, exibir placeholder com opção de upload + entrada manual
  if (!dadosFatura) {
    return (
      <Card className="overflow-hidden border-border bg-card">
        <CardHeader className="border-b bg-[#FBFAF9] px-3.5 py-2 dark:bg-white/[0.02]">
          <CardTitle className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.08em] text-card-foreground">
            <Building2 className="h-3 w-3 text-muted-foreground" />
            Conta atual · CNPJ
            <Badge variant="outline" className="ml-auto border-border bg-[#F2EEE8] text-[9px] font-bold uppercase text-muted-foreground dark:bg-white/[0.06]">
              Renovação
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-3">
          {isLoading ? (
            <div className="p-4 bg-card rounded-lg border border-dashed border-border text-center">
              <Loader2 className="w-8 h-8 mx-auto text-muted-foreground mb-2 animate-spin" />
              <p className="text-sm font-medium text-foreground">
                {isUploading ? 'Enviando fatura...' : 'Analisando fatura...'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Aguarde, isso pode levar alguns segundos
              </p>
            </div>
          ) : (
            <>
              {/* Upload de fatura */}
              <div className="p-3 bg-card rounded-lg border border-dashed border-border text-center">
                <FileText className="w-6 h-6 mx-auto text-muted-foreground/50 mb-1.5" />
                <p className="text-xs text-muted-foreground mb-2">Fatura ainda não analisada</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-[11px]"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="w-3.5 h-3.5 mr-1.5" />
                  Incluir Fatura Manualmente
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf,image/*"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </div>

              {/* Entrada manual de linhas */}
              <div className="p-3 bg-card rounded-lg border border-border">
                <div className="flex items-center gap-2 mb-3">
                  <PenLine className="w-3.5 h-3.5 text-muted-foreground" />
                  <p className="text-[9.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground">Incluir linhas manualmente</p>
                  <span className="text-[10px] text-muted-foreground ml-1">(para montar proposta sem fatura)</span>
                </div>

                <div className="space-y-2">
                  {linhasManuais.map((linha, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Smartphone className="w-3.5 h-3.5 text-primary/60 flex-shrink-0" />
                      <Input
                        type="tel"
                        placeholder="(00) 00000-0000"
                        value={linha.numero}
                        onChange={e => updateLinha(idx, 'numero', formatPhone(e.target.value))}
                        className="h-8 text-xs font-mono flex-1"
                      />
                      <Select
                        value={String(linha.gbAtual)}
                        onValueChange={val => updateLinha(idx, 'gbAtual', parseInt(val))}
                      >
                        <SelectTrigger className="h-8 text-xs w-24 flex-shrink-0">
                          <SelectValue placeholder="GB" />
                        </SelectTrigger>
                        <SelectContent>
                          {GB_OPTIONS.map(gb => (
                            <SelectItem key={gb} value={String(gb)} className="text-xs">
                              {gb === 0 ? '— GB' : `${gb} GB`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <span className="text-xs text-muted-foreground">R$</span>
                        <Input
                          type="number"
                          placeholder="0,00"
                          min={0}
                          step={0.01}
                          value={linha.valorAtual || ''}
                          onChange={e => updateLinha(idx, 'valorAtual', parseFloat(e.target.value) || 0)}
                          className="h-8 text-xs w-20 text-right"
                        />
                      </div>
                      {linhasManuais.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                          onClick={() => removeLinha(idx)}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>

                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-2 h-7 text-xs text-primary hover:bg-primary/10 w-full"
                  onClick={addLinha}
                >
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  Adicionar linha
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    );
  }

  // Calcular economia se houver proposta
  const economia = dadosFatura.valor_proposto ? dadosFatura.valor - dadosFatura.valor_proposto : 0;
  const economiaPercent = dadosFatura.valor > 0 && economia > 0 
    ? ((economia / dadosFatura.valor) * 100).toFixed(0) 
    : null;

  const competenciaUso = (dadosFatura as { competencia?: string } | null)?.competencia;
  const compFmt = competenciaUso && /^\d{4}-\d{2}/.test(competenciaUso) ? `${competenciaUso.slice(5, 7)}/${competenciaUso.slice(0, 4)}` : null;

  // TM de cada linha pela fidelidade na conta (fim da fidelidade − 24 meses).
  // Mesma régua da trava do pedido; sem fidelidade lida, a linha mostra "—".
  const linhasConta = (dadosFatura.linhasDetalhadas || []).map((linha) => ({
    ...linha,
    tm: tmPelaFidelidade(linha.dataFidelidade),
  }));
  const tms = linhasConta.map((l) => l.tm).filter((tm): tm is number => tm != null);
  const faixaTm = tms.length === 0 ? '—' : Math.min(...tms) === Math.max(...tms) ? `${tms[0]} m` : `${Math.min(...tms)}–${Math.max(...tms)} m`;
  const fimFidelidade = (data?: string | null) => {
    const m = String(data || '').match(/^(\d{4})-(\d{2})/);
    if (!m) return '—';
    const meses = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
    return `${meses[Number(m[2]) - 1]}/${m[1]}`;
  };
  const corTm = (tm: number | null) => tm == null
    ? 'border-[1.5px] border-[#A6907B] bg-transparent'
    : tm >= 18 ? 'bg-[#128C7E] dark:bg-[#25D366]'
      : tm >= 12 ? 'bg-[#9D2530] dark:bg-[#C87484]'
        : 'border-[1.5px] border-[#9D2530] bg-transparent dark:border-[#C87484]';
  const rotuloTm = (tm: number | null) => tm == null ? 'sem fidelidade lida' : tm >= 18 ? 'livre' : tm >= 12 ? 'exige aparelho' : 'só bloco';

  return (
    <div className={`relative overflow-hidden rounded-xl border border-border bg-card pl-1 ${ocultarPlanoAtual ? 'opacity-60' : ''}`}>
      <span aria-hidden className="absolute inset-y-0 left-0 w-1 bg-[linear-gradient(180deg,#6E1626,#4E1522_55%,#3E0E1B)]" />
      <div className="flex h-[34px] items-center justify-between gap-3 border-b border-border bg-[#FAF8F4] px-4 dark:bg-[#241A1A]">
        <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.08em] text-foreground">
          <Building2 className="h-3.5 w-3.5 text-muted-foreground" />Conta atual · CNPJ
        </p>
        <div className="flex items-center gap-2">
          <span className="text-[10.5px] text-muted-foreground">{compFmt ? `fatura ${compFmt} em uso` : 'fatura analisada em uso'}</span>
          {onToggleOcultar && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-muted-foreground hover:text-primary" onClick={onToggleOcultar}>
                    {ocultarPlanoAtual ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="left">
                  <p className="text-xs">{ocultarPlanoAtual ? 'Plano atual OCULTO na proposta. Clique para mostrar.' : 'Clique para OCULTAR o plano atual da proposta'}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>
      <div className="space-y-3 p-4">
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          {[
            [String(dadosFatura.linhas), 'linhas ativas'],
            [`${dadosFatura.gb} GB`, 'contratado'],
            [formatarMoeda(dadosFatura.valor), '/mês na fatura'],
            [faixaTm, 'TM das linhas'],
          ].map(([valor, rotulo]) => (
            <div key={rotulo} className="min-w-0 rounded-lg border border-border bg-card px-3 py-2">
              <p className="truncate font-display text-[19px] font-bold leading-tight tabular-nums text-primary dark:text-[#C87484]" title={String(valor)}>{valor}</p>
              <p className="text-[9px] font-bold uppercase tracking-[0.08em] text-muted-foreground">{rotulo}</p>
            </div>
          ))}
        </div>
        {linhasConta.length > 0 ? (
          <div className="overflow-hidden rounded-lg border border-border">
            <div className="grid h-7 grid-cols-[150px_minmax(0,1fr)_96px_104px_132px] items-center gap-3 bg-[#FAF8F4] px-3 text-[8.5px] font-bold uppercase tracking-[1.1px] text-muted-foreground dark:bg-[#241A1A]">
              <span>Linha</span><span>Plano</span><span className="text-right">Valor</span><span className="text-right">Fidelidade até</span><span className="text-right">TM</span>
            </div>
            {linhasConta.map((linha, idx) => (
              <div key={`${linha.numero}-${idx}`} className="grid h-9 grid-cols-[150px_minmax(0,1fr)_96px_104px_132px] items-center gap-3 border-t border-border px-3 text-[12.5px] odd:bg-card even:bg-[#FBF9F6] dark:even:bg-[#211818]">
                <span className="truncate font-bold tabular-nums text-foreground">{formatarTelefone(linha.numero)}</span>
                <span className="min-w-0 truncate text-muted-foreground" title={linha.planoAtual || ''}>{linha.planoAtual || `${linha.gbAtual}GB`}</span>
                <span className="text-right tabular-nums text-foreground">{linha.valorAtual ? formatarMoeda(linha.valorAtual) : '—'}</span>
                <span className="text-right tabular-nums text-muted-foreground">{fimFidelidade(linha.dataFidelidade)}</span>
                <span className="flex items-center justify-end gap-1.5 truncate tabular-nums" title={rotuloTm(linha.tm)}>
                  <span aria-hidden className={`h-2 w-2 flex-shrink-0 rounded-full ${corTm(linha.tm)}`} />
                  <b className="font-bold text-foreground">{linha.tm != null ? `${linha.tm} meses` : '—'}</b>
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Sem detalhe por linha na fatura analisada.</p>
        )}
        {dadosFatura.valor_proposto && dadosFatura.valor_proposto > 0 ? (
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-[#128C7E]/50 bg-[#D7EEE6] px-3 py-2 text-[12.5px] dark:border-[#25D366]/40 dark:bg-[#10261F]">
            <Zap className="h-4 w-4 flex-shrink-0 text-[#075E54] dark:text-[#25D366]" />
            <span className="min-w-[200px] flex-1 text-[#2E2019] dark:text-[#E8DEDE]">
              <b className="font-bold">Análise automática:</b> renovar as {dadosFatura.linhas} linhas por{' '}
              <b className="font-bold tabular-nums text-[#075E54] dark:text-[#25D366]">{formatarMoeda(dadosFatura.valor_proposto)}/mês</b>
              {dadosFatura.gb_proposto ? <> com <b className="font-bold tabular-nums">{dadosFatura.gb_proposto} GB</b></> : null}
              {economiaPercent ? <> — <b className="font-bold tabular-nums text-[#075E54] dark:text-[#25D366]">−{economiaPercent}%</b> sobre a fatura</> : null}
            </span>
            {onAplicarSugestao && (
              <button
                type="button"
                onClick={onAplicarSugestao}
                className="h-8 flex-shrink-0 rounded-lg bg-[linear-gradient(90deg,#4A1320,#5B0A0D)] px-3 text-[12px] font-bold text-white shadow-[0_2px_6px_rgba(91,10,13,0.30)] transition-all hover:brightness-110 dark:bg-[linear-gradient(90deg,#9D2530,#6C2236)]"
              >
                Aplicar nas linhas
              </button>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
